import React, { useMemo } from 'react'
import { graphql, useFragment } from 'react-relay'

import { YStack, XStack, View, getTokenValue } from 'tamagui'

import { useInEmbed } from '../Embed'

import { Link } from '../Link'
import { H3, Text } from '../Text'
import { Card } from '../Card'
import { Image } from '../Image'
import { Button } from '../Button/Button'
import { FormattedMarkdown } from '../FormattedMarkdown/FormattedMarkdown'
import { UserPrimaryPhoto } from './UserPrimaryPhoto'
import { GuildPrimaryPhoto } from '../Guild/GuildPrimaryPhoto'
import { Icon } from '../Icon'

import { UserCardNew_user$key } from './__generated__/UserCardNew_user.graphql'
import { UserCardNew_GuildMemberships$key } from './__generated__/UserCardNew_GuildMemberships.graphql'

const ALLOWED_ELEMENTS = ['a', 'p']

// CSS gradient fallbacks for users without a background photo
// (same approach as legacy UserBackgroundPhoto)
const DEFAULT_BACKGROUNDS = [
  'radial-gradient(114% 86.44% at 93.08% -9.01%, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0) 100%), radial-gradient(65.03% 49.37% at 67.2% -1.1%, rgba(97, 227, 255, 0.7) 0%, rgba(97, 227, 255, 0) 100%), radial-gradient(73.71% 91.32% at 0% 100%, rgba(228, 213, 79, 0.7) 0%, rgba(228, 79, 79, 0) 100%), radial-gradient(62.83% 107.44% at 97.15% 97.09%, rgba(228, 79, 79, 0.7) 0%, rgba(228, 79, 79, 0) 100%), radial-gradient(99.64% 75.06% at 8.54% 18.33%, rgba(85, 51, 235, 0.7) 0%, rgba(85, 51, 235, 0) 100%), #E5ADFF',
  'radial-gradient(114% 86.44% at 93.08% -9.01%, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0) 100%), radial-gradient(62.83% 107.44% at 97.15% 97.09%, rgba(228, 79, 79, 0.7) 0%, rgba(228, 79, 79, 0) 100%), radial-gradient(99.64% 75.06% at 8.54% 18.33%, rgba(85, 51, 235, 0.7) 0%, rgba(85, 51, 235, 0) 100%), linear-gradient(0deg, #E5ADFF, #E5ADFF), radial-gradient(114% 86.44% at 93.08% -9.01%, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0) 100%), radial-gradient(65.03% 49.37% at 67.2% -1.1%, rgba(97, 227, 255, 0.7) 0%, rgba(97, 227, 255, 0) 100%), radial-gradient(73.71% 91.32% at 0% 100%, rgba(228, 213, 79, 0.7) 0%, rgba(228, 79, 79, 0) 100%)',
]

function pickFromDefaults(rowId: string | undefined, defaults: string[]) {
  const id = rowId?.substring(0, 8) || '0'
  return defaults[parseInt(id, 16) % defaults.length]
}

export function UserCard({
  user,
  limitDescriptionLines = 4,
  showBadges = true,
  expand = false,
}: {
  user: UserCardNew_user$key
  limitDescriptionLines?: number
  showBadges?: boolean
  expand?: boolean
}) {
  const data = useFragment(
    graphql`
      fragment UserCardNew_user on User {
        rowId
        slugId
        firstName
        lastName
        bio

        isMe

        ...UserPrimaryPhotoNew_user

        backgroundPhoto {
          transformUrl(
            transform: { w: 500, h: 167, dpr: 3, fit: "cover" }
          )
        }

        organizerGuildMemberships: guildMemberships(
          first: 3
          condition: { membershipLevel: ORGANIZER }
        ) {
          ...UserCardNew_GuildMemberships
        }
        editorGuildMemberships: guildMemberships(
          first: 3
          condition: { membershipLevel: EDITOR }
        ) {
          ...UserCardNew_GuildMemberships
        }
      }
    `,
    user
  )

  const inEmbed = useInEmbed()

  const content = (
    <Card padding={0} flex={expand ? 1 : undefined}>
      <UserCardPhotos
        rowId={data.rowId}
        firstName={data.firstName}
        lastName={data.lastName}
        backgroundPhotoUrl={data.backgroundPhoto?.transformUrl ?? null}
        userFragmentRef={data}
      />

      <YStack
        gap="$4"
        paddingVertical="$4"
        paddingHorizontal="$5"
        flex={expand ? 1 : undefined}
      >
        <H3 size="$6">
          {data.firstName} {data.lastName}
        </H3>

        {!!data.bio && (
          <YStack flex={expand ? 1 : undefined}>
            <FormattedMarkdown
              allowedElements={ALLOWED_ELEMENTS}
              formattedVariant="PLAIN_TEXT"
              numberOfLines={limitDescriptionLines}
              textSize="$4"
            >
              {data.bio}
            </FormattedMarkdown>
          </YStack>
        )}

        {showBadges && (
          <BadgesSection
            organizerGuildMemberships={data.organizerGuildMemberships}
            editorGuildMemberships={data.editorGuildMemberships}
          />
        )}

        {inEmbed && !data.isMe && (
          <Button
            tag="a"
            href={`/${data.slugId}`}
            secondary
            height={34}
            borderRadius={17}
          >
            <Button.Text size="$4" secondary>
              View {data.firstName}'s Profile
            </Button.Text>
          </Button>
        )}

        {!inEmbed && data.isMe && (
          <XStack justifyContent="flex-end" alignItems="center" marginTop="$1" height={16}>
            <Link
              href={`/${data.slugId}/settings`}
              hoverStyle={{ textDecorationLine: 'none' }}
            >
              <Button
                borderRadius={15}
                marginRight={-11}
                height={30}
                minHeight={30}
                paddingRight="$3"
                paddingLeft="$2"
                secondary
              >
                <XStack gap="$1" alignItems="center">
                  <Icon size="$4" name="settings-filled" />

                  <Button.Text size="$4" secondary>
                    Settings
                  </Button.Text>
                </XStack>
              </Button>
            </Link>
          </XStack>
        )}
      </YStack>
    </Card>
  )

  return (
    <Link
      display="flex"
      flex={expand ? 1 : undefined}
      href={`/${data.slugId}`}
      hoverStyle={{ textDecorationLine: 'none' }}
      color="$colorStrong"
      flexDirection="column"
      size="$4"
    >
      {content}
    </Link>
  )
}

function UserCardPhotos({
  rowId,
  firstName,
  lastName,
  backgroundPhotoUrl,
  userFragmentRef,
}: {
  rowId: string
  firstName: string | null
  lastName: string | null
  backgroundPhotoUrl: string | null
  userFragmentRef: any
}) {
  const backgroundGradient = useMemo(() => {
    if (backgroundPhotoUrl) return null
    return pickFromDefaults(rowId, DEFAULT_BACKGROUNDS)
  }, [backgroundPhotoUrl, rowId])

  return (
    <YStack maxWidth="100%">
      {backgroundPhotoUrl ? (
        <Image
          alt={`Background Photo for ${firstName} ${lastName}`}
          src={backgroundPhotoUrl}
          aspectRatio={500 / 167}
          borderTopLeftRadius="$6"
          borderTopRightRadius="$6"
          maxHeight={30 * 4}
          maxWidth="100%"
          style={{ objectFit: 'cover', objectPosition: 'center' }}
        />
      ) : (
        <View
          borderTopLeftRadius="$6"
          borderTopRightRadius="$6"
          height={30 * 4}
          maxWidth="100%"
          style={{ background: backgroundGradient ?? undefined }}
        />
      )}

      <XStack marginTop={-40} paddingHorizontal="$5">
        <UserPrimaryPhoto
          user={userFragmentRef}
          size="$20"
          // @ts-expect-error boxShadow is web-only
          boxShadow="0 0 0 3px var(--background)"
        />
      </XStack>
    </YStack>
  )
}

const MEMBERSHIPS_FRAGMENT = graphql`
  fragment UserCardNew_GuildMemberships on GuildMembershipsConnection {
    edges {
      node {
        id
        membershipLevel
        guild {
          slugId
          ...GuildPrimaryPhotoNew_guild
        }
      }
    }
  }
`

function BadgesSection({
  organizerGuildMemberships,
  editorGuildMemberships,
}: {
  organizerGuildMemberships: UserCardNew_GuildMemberships$key
  editorGuildMemberships: UserCardNew_GuildMemberships$key
}) {
  const organizerData = useFragment(
    MEMBERSHIPS_FRAGMENT,
    organizerGuildMemberships
  )
  const editorData = useFragment(MEMBERSHIPS_FRAGMENT, editorGuildMemberships)

  const badges = useMemo(() => {
    return [
      ...organizerData.edges.filter(
        (e) => e.node?.guild?.slugId && e.node?.guild
      ),
      ...editorData.edges.filter(
        (e) => e.node?.guild?.slugId && e.node?.guild
      ),
    ].slice(0, 4)
  }, [organizerData, editorData])

  if (!badges.length) return null

  return (
    <XStack gap="$3" alignItems="flex-start">
      {badges.map((edge) => {
        if (!edge.node?.guild?.slugId) return null
        return (
          <BadgeLink
            key={edge.node.id}
            guild={edge.node.guild}
            slugId={edge.node.guild.slugId}
            membershipLevel={edge.node.membershipLevel}
          />
        )
      })}
    </XStack>
  )
}

function BadgeLink({
  guild,
  slugId,
  membershipLevel,
}: {
  guild: any
  slugId: string
  membershipLevel: string | null
}) {
  const badgeSize = getTokenValue('$10', 'size')
  const iconContainerSize = getTokenValue('$5', 'size')

  return (
    <Link href={`/${slugId}`}>
      <View height={badgeSize}>
        <GuildPrimaryPhoto guild={guild} size="$10" />

        {(membershipLevel === 'ORGANIZER' || membershipLevel === 'EDITOR') && (
          <View
            position="absolute"
            bottom={-iconContainerSize * 0.25}
            left={badgeSize - iconContainerSize * 0.75}
            width={iconContainerSize}
            height={iconContainerSize}
            alignItems="center"
            justifyContent="center"
            borderRadius={iconContainerSize / 2}
            // @ts-expect-error web-only styles
            boxShadow="inset 0px 0px 0px 3px rgba(27, 19, 64, 0.2)"
            style={{
              background:
                'linear-gradient(0deg, #5533EB 28.13%, rgba(85, 51, 235, 0) 159.37%), #FFFFFF',
            }}
          >
            <Icon
              name={
                membershipLevel === 'ORGANIZER' ? 'star-filled' : 'ribbon-filled'
              }
              size="$3"
              color="$colorOnPrimary"
            />
          </View>
        )}
      </View>
    </Link>
  )
}
